/**
 * Describe the inputs instead of filling the forms.
 *
 * A pipeline with a probability against each programme, a schedule of buildings and what
 * each is worth, a term sheet's exit assumptions — these arrive as prose, in an email or
 * a paragraph of a memo, and typing them into a grid is the tedious part. So they can be
 * pasted.
 *
 * NOTHING IS APPLIED UNTIL IT IS ACCEPTED, and the figures are shown beside the
 * description that produced them. Three reasons, in order:
 *
 *   The figures ARE the valuation. A misread cap rate is not a cosmetic error.
 *   A parse cannot be checked by arithmetic — there is no identity that says a
 *   paragraph was read correctly, unlike everything in the accounting engine.
 *   The valuation half of this product has no reference to check itself against, which
 *   `app/valuation/v2/__init__.py` records, so anything an AI puts into it has to
 *   arrive visibly.
 *
 * WHAT IT DID NOT FIND IS THE IMPORTANT HALF of the output and is shown in full. A
 * description with no cap rate in it must leave AFFO saying it needs one; a plausible
 * invented rate would produce a valuation nobody chose, and would look exactly like one
 * somebody did.
 */

import { useState } from 'react';
import { WorkingIndicator } from '../../../components/WorkingIndicator';
import { useAuthStore } from '../../../store/auth';

interface Parsed {
  methodInputs: Record<string, unknown>;
  understood: string[];
  missing: string[];
  ignored: string[];
}

const PLACEHOLDER = `For example:

Two properties — Mill Street, valued at £4.2m, and the yard at £1.25m.
Rent is £900k a year, £100k of that goes on upkeep, and the market is
capitalising these at 5.5%. There's £2m of debt against them.`;

export default function DescribeMethodInputs({
  onAccept,
}: {
  /**
   * The parsed blocks, merged over whatever is already saved by the caller.
   *
   * Merged rather than replacing: someone who filled in a NAV by hand and then
   * describes a pipeline should end with both.
   */
  onAccept: (methodInputs: Record<string, unknown>) => void;
}) {
  const [description, setDescription] = useState('');
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const read = async () => {
    setIsReading(true);
    setError(null);
    setParsed(null);
    try {
      const token = useAuthStore.getState().token;
      const response = await fetch(
        '/api/v2/models/valuation/parse-method-inputs',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ description }),
        },
      );
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(
          detail.detail ?? `That could not be read (${response.status})`,
        );
      }
      setParsed(await response.json());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setIsReading(false);
    }
  };

  const found = parsed ? Object.keys(parsed.methodInputs) : [];

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/60 p-4">
      <h3 className="text-sm font-medium text-gray-900">Describe them instead</h3>
      <p className="mt-1 max-w-2xl text-xs leading-relaxed text-gray-600">
        Paste a note, an email or a paragraph and the figures will be pulled out for you
        to check. Nothing is filled in until you accept it, and anything the description
        does not say is reported rather than guessed.
      </p>

      <textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder={PLACEHOLDER}
        rows={6}
        className="mt-3 w-full rounded border border-gray-300 px-2.5 py-2 text-sm"
      />

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={isReading || description.trim() === ''}
          onClick={() => void read()}
          className="rounded bg-gray-900 px-3 py-1.5 text-xs text-white disabled:opacity-40"
        >
          {isReading ? 'Reading…' : 'Read this'}
        </button>
        {parsed && (
          <button
            type="button"
            disabled={found.length === 0}
            onClick={() => {
              onAccept(parsed.methodInputs);
              setParsed(null);
              setDescription('');
            }}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 hover:bg-gray-50 disabled:opacity-40"
          >
            {found.length === 0
              ? 'Nothing to fill in'
              : `Fill in ${found.length} method${found.length === 1 ? '' : 's'}`}
          </button>
        )}
      </div>

      {isReading && (
        <div className="mt-3 text-xs text-blue-900">
          <WorkingIndicator label="Reading the description" />
        </div>
      )}

      {error && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {error}
        </div>
      )}

      {parsed && (
        <div className="mt-3 space-y-2 text-xs">
          {parsed.understood.length > 0 && (
            <div>
              <div className="font-medium text-gray-800">What it took</div>
              {/* Each figure in words, so it can be read against the paragraph above.
                  This is the review step, and it is the reason nothing is applied on
                  arrival. */}
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-gray-600">
                {parsed.understood.map((entry) => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            </div>
          )}

          {parsed.missing.length > 0 && (
            <div>
              <div className="font-medium text-amber-900">
                What it did not find, and did not invent
              </div>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-amber-800">
                {parsed.missing.map((entry) => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            </div>
          )}

          {parsed.ignored.length > 0 && (
            <div>
              <div className="font-medium text-gray-700">What it could not use</div>
              {/* Shown because a user who described something and saw no sign of it
                  will assume it was understood. */}
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-gray-500">
                {parsed.ignored.map((entry) => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            </div>
          )}

          {found.length === 0 && parsed.missing.length === 0 && (
            <p className="text-gray-600">
              Nothing in that description matched a method this engine has. The forms below take
              the figures directly.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
